import { HeaderHelperService, HeaderPositionModeEnum, HeaderVisibilityChangePubsubMessage } from '@alliance/shared/header/utils'
import { RxStateComponent } from '@alliance/shared/models'
import { DetectPlatformService, NgZoneHelperService, NotificationAlertRerenderConfigEventPayload, PubsubMessageTypesEnum, TransferService } from '@alliance/shared/utils'
import { DOCUMENT } from '@angular/common'
import { AfterViewInit, ChangeDetectionStrategy, Component, ContentChild, Inject, Input, OnDestroy, Renderer2, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core'
import { Overlay, OverlayRef } from '@angular/cdk/overlay'
import { TemplatePortal } from '@angular/cdk/portal'
import { PolymorpheusContent } from '@alliance/shared/polymorpheus'
import { asapScheduler, fromEvent, Observable, of } from 'rxjs'
import { filter, map, pairwise, share, startWith, throttleTime } from 'rxjs/operators'

@Component({
  selector: 'alliance-feature-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent
  extends RxStateComponent<{
    isHidden: boolean
    positionMode: HeaderPositionModeEnum | null
    headerHeight: number
    hideHeaderTransfer: boolean | undefined
  }>
  implements AfterViewInit, OnDestroy
{
  @ViewChild('templatePortalContent') public templatePortalContent: TemplateRef<unknown> | undefined
  @ContentChild('centerContent') public centerContent: PolymorpheusContent | undefined
  @ContentChild('botContent') public botContent: PolymorpheusContent | undefined
  @ContentChild('topContent') public topContent: PolymorpheusContent | undefined

  private readonly TOP_OFFSET_FOR_PREVENT_HIDE = 200
  private overlayRef: OverlayRef | undefined

  public constructor(
    @Inject(DOCUMENT) private document: Document,
    public platform: DetectPlatformService,
    private overlay: Overlay,
    private renderer: Renderer2,
    private transfer: TransferService,
    private _viewContainerRef: ViewContainerRef,
    private ngZoneHelper: NgZoneHelperService,
    private headerHelperService: HeaderHelperService
  ) {
    super()

    this.initState({
      hideHeaderTransfer: this.transfer.subscribeTo<boolean>(PubsubMessageTypesEnum.hideHeader),
      positionMode: null,
      headerHeight: 0,
      isHidden: this.getIsHidden$()
    })

    this.hold(this.select('isHidden'), isHidden => {
      this.renderer.setStyle(this.overlayRef?.overlayElement, 'transform', `translateY(${isHidden ? '-100%' : '0'})`)
      this.transfer.publish<HeaderVisibilityChangePubsubMessage>(PubsubMessageTypesEnum.headerVisibilityChange, {
        isHidden,
        height: this.get().headerHeight
      })
    })

    this.hold(
      this.getScrollPosition().pipe(
        filter(() => this.get().positionMode === HeaderPositionModeEnum.static),
        this.ngZoneHelper.outsideNgZone<number>()
      ),
      scrollPosition => {
        this.renderer.setStyle(this.overlayRef?.overlayElement, 'transform', `translateY(-${scrollPosition > this.get().headerHeight ? this.get().headerHeight : scrollPosition}px)`)
      }
    )

    this.hold(this.select('positionMode'), () => this.changeHeaderStyle())
    this.hold(this.transfer.subscribeTo(PubsubMessageTypesEnum.headerForNotifications), () => {
      this.transfer.publish<HeaderVisibilityChangePubsubMessage>(PubsubMessageTypesEnum.headerVisibilityChange, {
        isHidden: false,
        height: this.get().headerHeight
      })
    })
  }

  @Input()
  public set positionMode(value: HeaderPositionModeEnum | null) {
    this.set({ positionMode: value })
  }

  public ngAfterViewInit(): void {
    this.renderHeader()
  }

  // fires every time the header changes its structure
  public onHeaderMutation(): void {
    this.changeHeaderStyle()
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy()

    if (this.platform.isBrowser) {
      if (this.get().positionMode !== HeaderPositionModeEnum.inline) {
        this.renderer.removeStyle(this.document.body, 'padding-top')
      }
      this.overlayRef?.detach()
      this.overlayRef?.dispose()
    }
  }

  private getIsHidden$(): Observable<boolean> {
    return this.getScrollPosition().pipe(
      filter(() => this.get().positionMode === HeaderPositionModeEnum.sticky),
      startWith(this.document.defaultView?.pageYOffset || 0),
      throttleTime(100, asapScheduler, { trailing: true }),
      map(() => this.document.defaultView?.pageYOffset || 0),
      pairwise(),
      map(([prev, current]) => {
        // если hideHeaderTransfer true - скрывать хедер
        // пример использования libs/jobseeker/vac-list/feature-ui/src/lib/vacancy-card-base/vacancy-card-base.component.ts 386
        if (this.get().hideHeaderTransfer) {
          this.set({ hideHeaderTransfer: false })
          return true
        }
        return current > prev && current >= this.TOP_OFFSET_FOR_PREVENT_HIDE
      }),
      this.ngZoneHelper.outsideNgZone()
    )
  }

  private getScrollPosition(): Observable<number> {
    return this.document.defaultView
      ? fromEvent(this.document.defaultView, 'scroll').pipe(
          map(() => this.document.defaultView?.pageYOffset || 0),
          share()
        )
      : of(0)
  }

  private attachPortal(): void {
    this.overlayRef = this.overlay.create({
      width: '100%'
    })
    if (this.templatePortalContent) {
      this.overlayRef.attach(new TemplatePortal(this.templatePortalContent, this._viewContainerRef))
    }
  }

  private changeHeaderStyle(): void {
    if (this.overlayRef?.overlayElement) {
      const overlayElement: HTMLElement | null = this.overlayRef.overlayElement

      // reset style
      this.renderer.removeStyle(overlayElement, 'height')
      this.renderer.removeStyle(overlayElement, 'transition')
      this.renderer.removeStyle(overlayElement, 'transform')
      this.renderer.removeStyle(overlayElement, 'will-change')

      // add new style
      const headerHeight = overlayElement.getBoundingClientRect()?.height || 0
      this.set({ headerHeight })
      this.transfer.publish<HeaderVisibilityChangePubsubMessage>(PubsubMessageTypesEnum.headerVisibilityChange, {
        isHidden: false,
        height: this.get().headerHeight
      })

      // add overlay height
      if (this.get().positionMode !== HeaderPositionModeEnum.inline) {
        this.renderer.removeStyle(this.document.body, 'padding-top')
        // IMPORTANT: please, do not add important flag for desktop
        // there is no way to overwrite these styles if needed -> style attribute is already heavy enough
        this.renderer.setStyle(this.document.body, 'padding-top', `${headerHeight}px`)
      }

      this.renderer.setStyle(overlayElement, 'height', `${headerHeight}px`)

      // add overlay transform
      if (this.get().positionMode === HeaderPositionModeEnum.sticky) {
        this.renderer.setStyle(overlayElement, 'transition', 'transform 0.25s ease-in-out')
        this.renderer.setStyle(overlayElement, 'will-change', 'transform')
      }

      if (this.get().positionMode === HeaderPositionModeEnum.inline) {
        this.renderer.setStyle(this._viewContainerRef.element.nativeElement, 'height', `${headerHeight}px`)
      }

      this.headerHelperService.setOverlayCreated()
    }
  }

  private renderHeader(): void {
    if (this.platform.isBrowser && this.templatePortalContent) {
      this.attachPortal()

      // custom event
      this.transfer.publish<boolean>(PubsubMessageTypesEnum.headerIsRendered, !!this.overlayRef)
      this.transfer.publish<HTMLElement | null>(PubsubMessageTypesEnum.htmlElementsHeader, this.overlayRef ? this.overlayRef.overlayElement : null)
      this.transfer.publish<NotificationAlertRerenderConfigEventPayload>(PubsubMessageTypesEnum.rerenderNotificationAlert, {
        recreateOverlay: true
      })
    }
  }
}
